import CloseIcon from './CloseIcon.jsx';
import { useState, useRef, useEffect } from 'react';
import { useModalFocus } from '../hooks/useModalFocus.js';
import { createPortal } from 'react-dom';

// PopoutModal, full-viewport reading overlay for content that is legible but
// cramped in the narrow columns (Tonight hero briefing, per-target briefings).
// Draggable by its header (ReentryDrawer pattern); × and click-outside close.
//
// Two hard-won rules live here:
// 1. PORTAL TO <body>. The columns that mount these modals carry their own
//    z-index (stacking contexts), so any z-index set here would only win
//    WITHIN the column, the top bar (z 50) painted over the modal's header,
//    hiding close/drag. createPortal escapes the trap entirely.
// 2. CENTERED until dragged. pos === null renders a transform-centered panel;
//    the first drag captures the live rect and switches to pixel coords, so
//    the handoff is visually seamless.

export default function PopoutModal({ title, onClose, children, closeLabel="Close", className }) {
  const dialog = useRef(null);
  useModalFocus(dialog, onClose);
  const [pos, setPos] = useState(null); // null → centered; {x,y} after drag

  useEffect(()=>{const reset=()=>setPos(null);window.addEventListener('resize',reset);return()=>window.removeEventListener('resize',reset);},[]);

  function startDrag(e) {
    if (e.button !== 0) return;
    if (e.target.closest('button')) return; // the × still just closes
    e.preventDefault();
    const panel = e.currentTarget.parentElement;
    const rect = panel.getBoundingClientRect();
    const start = pos || { x: rect.left, y: rect.top };
    if (!pos) setPos(start);
    const sx = e.clientX - start.x, sy = e.clientY - start.y;
    const W = rect.width;
    function onMove(ev) {
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - W, ev.clientX - sx)),
        y: Math.max(0,      Math.min(window.innerHeight - rect.height, ev.clientY - sy)),
      });
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const placement = pos
    ? { left: pos.x, top: pos.y }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        ref={dialog} className={['og-popout-dialog',className].filter(Boolean).join(' ')} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
        style={{
          position: 'absolute', ...placement,
          width: 'min(640px, calc(100vw - 32px))',
          maxHeight: '85vh', overflowY: 'auto',
          background: 'var(--bg-2)', border: '1px solid var(--hair-2)',
          borderRadius: 10, padding: '0 20px 20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.55)',
        }}
      >
        <div
          onMouseDown={startDrag}
          title="Drag to move"
          style={{
            position: 'sticky', top: 0, background: 'var(--bg-2)', zIndex: 1,
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 0 10px', borderBottom: '1px solid var(--hair)',
            cursor: 'move', userSelect: 'none',
          }}
        >
          <button title="Close" className="og-close-control"
            onClick={onClose} aria-label={closeLabel}
            style={{
              width: 44, height: 44, flexShrink: 0, background: 'var(--bg-2)',
              border: '1px solid var(--hair-2)', borderRadius: 4,
              color: 'var(--accent)', fontSize: 15, cursor: 'pointer',
            }}
          ><CloseIcon /></button>
          <h2 style={{
            fontFamily: 'var(--mono)', fontSize: 'var(--ui-body)', fontWeight: 700,
            letterSpacing: '0.1em', color: 'var(--txt-bright)',
            textTransform: 'uppercase', margin: 0, flex: 1,
            overflowWrap: 'anywhere',
          }}>{title}</h2>
          <i className="ti ti-grip-horizontal" aria-hidden="true"
             style={{ color: 'var(--txt-mute)', fontSize: 'var(--ui-body)', flexShrink: 0 }} />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}